import { Link } from "react-router-dom";
import { ArrowUpRight, Leaf, MapPin } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useClimateProjects } from "@/hooks/useClimateProjects";
import { cn } from "@/lib/utils";
import type { ClimateProjectRow, ClimateProjectStatus } from "@/lib/types";

/**
 * Dashboard widget — horizontal strip of up to 6 active climate projects.
 * Lives below the stat cards. Compact: each card is ~14rem wide, the
 * strip is horizontally scrollable on narrow screens. Filters server-side
 * to status=active so the dashboard surfaces "what's happening now".
 *
 * Auto-hides when there are no active projects (the full library is one
 * sidebar click away regardless).
 */
export function ClimateProjectsStrip() {
  const list = useClimateProjects({ status: "active", page: 1 });
  const items = (list.data?.results ?? []).slice(0, 6);

  if (list.isLoading) {
    return (
      <Card className="border-border/60 shadow-soft">
        <CardHeader className="border-b border-border/60 py-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Leaf className="h-3.5 w-3.5 text-muted-foreground" />Active projects
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3">
          <div className="flex gap-3 overflow-x-auto">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-32 w-56 shrink-0" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }
  if (items.length === 0) return null;

  return (
    <Card className="border-border/60 shadow-soft">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 border-b border-border/60 py-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Leaf className="h-3.5 w-3.5 text-muted-foreground" />
            Active climate projects
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-mono tabular-nums text-muted-foreground">
              {list.data?.count ?? 0}
            </span>
          </CardTitle>
        </div>
        <Link
          to="/climate-projects"
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          View all
          <ArrowUpRight className="h-3 w-3" />
        </Link>
      </CardHeader>

      <CardContent className="p-3">
        {/* Horizontal scroll strip — fixed-width tiles so the row stays
            compact regardless of how many projects fit. */}
        <ul className="flex gap-3 overflow-x-auto pb-1 [scrollbar-width:thin]">
          {items.map((p) => <ProjectTile key={p.id} project={p} />)}
        </ul>
      </CardContent>
    </Card>
  );
}

function ProjectTile({ project: p }: { project: ClimateProjectRow }) {
  return (
    <li className="shrink-0">
      <Link
        to={`/climate-projects/${p.id}`}
        className="group block w-56 overflow-hidden rounded-md border border-border bg-card transition hover:-translate-y-0.5 hover:shadow-soft"
      >
        <div className="relative aspect-[16/9] bg-gradient-to-br from-primary/15 to-emerald-700/10">
          {p.image_url ? (
            <img src={p.image_url} alt="" loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
          ) : (
            <div className="flex h-full items-center justify-center">
              <Leaf className="h-7 w-7 text-primary/40" />
            </div>
          )}
          <span className={cn(
            "absolute left-1.5 top-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold backdrop-blur",
            statusClass(p.status),
          )}>
            {p.status_display}
          </span>
        </div>
        <div className="p-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {p.initiative}
          </div>
          <div className="mt-0.5 line-clamp-2 text-xs font-semibold group-hover:text-primary">
            {p.title}
          </div>
          {p.location && (
            <div className="mt-1 inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
              <MapPin className="h-2.5 w-2.5" />{p.location}
            </div>
          )}
        </div>
      </Link>
    </li>
  );
}

function statusClass(status: ClimateProjectStatus): string {
  if (status === "active")    return "bg-emerald-500/90 text-white";
  if (status === "completed") return "bg-blue-500/90 text-white";
  return "bg-amber-500/90 text-white";
}
